import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/auth-store';
import { supabase } from '../supabase';

export type Subscription = 'free' | 'pro' | 'premium';

interface SubscriptionLimits {
  maxNotes: number;
  maxStorage: number; // in bytes
  maxCharacters: number;
}

const SUBSCRIPTION_LIMITS: Record<Subscription, SubscriptionLimits> = {
  free: {
    maxNotes: 8,
    maxStorage: 500 * 1024, // 500KB total
    maxCharacters: 5000
  },
  pro: {
    maxNotes: 20,
    maxStorage: 5 * 1024 * 1024, // 5MB total
    maxCharacters: 8000
  },
  premium: {
    maxNotes: 35,
    maxStorage: 15 * 1024 * 1024, // 15MB total
    maxCharacters: 10000
  }
};

export const useSubscription = () => {
  const { user } = useAuthStore();
  const [subscription, setSubscription] = useState<Subscription>('free');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [storageUsed, setStorageUsed] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('subscription, subscription_expiry, storage_used')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setSubscription(data.subscription as Subscription);
          setExpiryDate(data.subscription_expiry ? new Date(data.subscription_expiry) : null);
          setStorageUsed(data.storage_used || 0);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const getLimits = () => SUBSCRIPTION_LIMITS[subscription];

  const checkLimit = (type: keyof SubscriptionLimits, value: number) => {
    const limits = getLimits();
    return value <= limits[type];
  };

  const getStorageUsed = async () => {
    if (!user) return 0;

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('storage_used')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      const used = data?.storage_used || 0;
      setStorageUsed(used);
      return used;
    } catch (error) {
      console.error('Error fetching storage used:', error);
      return 0;
    }
  };

  return {
    subscription,
    expiryDate,
    isLoading,
    limits: getLimits(),
    checkLimit,
    getStorageUsed,
    storageUsed
  };
};